import { ChangeEvent, useState } from 'react';

import DeleteModal from '@atom/components/common/DeleteModal';
import { Icon, IconButton, Progress, TextField } from '@atom/mui';
import {
  CheckBoxIcon,
  CheckBoxOutlineBlankIcon,
  CloseIcon,
  DeleteIcon,
  FileDownloadIcon,
  IndeterminateCheckBoxIcon,
  PublishIcon,
  SearchIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';

import classes from './photosTab.module.css';

const UPLOAD_INPUT_ID = 'photosTabUploadInput';

const styles = {
  closeIcon: {
    cursor: 'pointer',
  },
  progress: {
    marginRight: '0.5rem',
  },
};

export type PhotosHeaderProps = {
  photoCount: number;
  selectedCount: number;
  search: string;
  loading: boolean;
  canCreateMedia: boolean;
  canDeleteMedia: boolean;
  onSearchChange: (search: string) => void;
  onUpload: (files: FileList) => void;
  onDownloadSelected: () => void;
  onDeleteSelected: () => Promise<void>;
  onSelectAll: () => void;
  onDeselectAll: () => void;
};

const PhotosHeader = ({
  photoCount,
  selectedCount,
  search,
  loading,
  canCreateMedia,
  canDeleteMedia,
  onSearchChange,
  onUpload,
  onDownloadSelected,
  onDeleteSelected,
  onSelectAll,
  onDeselectAll,
}: PhotosHeaderProps) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const handleDeleteSelected = async () => {
    await onDeleteSelected();
    setDeleteModalOpen(false);
  };

  const handleUpload = (event: ChangeEvent<HTMLInputElement>) => {
    onUpload(event.target.files);
    event.target.value = '';
  };

  const hasSelection = selectedCount > 0;

  const selectAllState = hasSelection
    ? {
        icon:
          selectedCount === photoCount
            ? CheckBoxIcon
            : IndeterminateCheckBoxIcon,
        color: colors.brand.blue,
        message: `Selected ${selectedCount} photos`,
        onClick: onDeselectAll,
      }
    : {
        icon: CheckBoxOutlineBlankIcon,
        color: colors.neutral.gray,
        message: 'Select all',
        onClick: onSelectAll,
      };

  const defaultHeaderButtons = (
    <>
      <TextField
        value={search}
        onChange={event => onSearchChange(event.target.value)}
        placeholder="Search"
        variant="standard"
        data-cy="photosTabSearchInput"
        slotProps={{
          input: {
            startAdornment: <Icon icon={SearchIcon} />,
            endAdornment: (
              <Icon
                icon={CloseIcon}
                style={styles.closeIcon}
                onClick={() => onSearchChange('')}
                data-cy="photosTabSearchClearButton"
              />
            ),
          },
        }}
      />
      {canCreateMedia && (
        <div>
          <input
            id={UPLOAD_INPUT_ID}
            className={classes.hiddenFileInput}
            multiple
            type="file"
            onChange={handleUpload}
            data-cy="photosTabUploadInput"
          />
          <label htmlFor={UPLOAD_INPUT_ID}>
            <IconButton
              component="span"
              tooltip="Upload"
              data-cy="photosTabUploadButton"
            >
              <Icon icon={PublishIcon} />
            </IconButton>
          </label>
        </div>
      )}
    </>
  );

  const bulkHeaderButtons = (
    <>
      <IconButton
        onClick={onDownloadSelected}
        tooltip="Download"
        data-cy="photosTabDownloadSelectedButton"
      >
        <Icon icon={FileDownloadIcon} />
      </IconButton>
      {canDeleteMedia && (
        <IconButton
          onClick={() => setDeleteModalOpen(true)}
          tooltip="Delete"
          data-cy="photosTabDeleteSelectedButton"
        >
          <Icon icon={DeleteIcon} />
        </IconButton>
      )}
      <DeleteModal
        open={deleteModalOpen}
        title="Delete Files"
        content="Are you sure you want to delete these photos?"
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteSelected}
      />
    </>
  );

  return (
    <div className={classes.rightPaneHeader} data-cy="photosTabHeader">
      {photoCount ? (
        <div className={classes.selectAllButtons}>
          <IconButton
            onClick={selectAllState.onClick}
            data-cy="photosTabSelectAllButton"
          >
            <Icon color={selectAllState.color} icon={selectAllState.icon} />
          </IconButton>
          <div style={{ color: selectAllState.color }}>
            {selectAllState.message}
          </div>
        </div>
      ) : (
        <div>{`${photoCount} photos`}</div>
      )}
      <div className={classes.buttonContainer}>
        {loading && <Progress size={20} style={styles.progress} />}
        {hasSelection ? bulkHeaderButtons : defaultHeaderButtons}
      </div>
    </div>
  );
};

export default PhotosHeader;
